import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { StrictMode } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoginForm } from "@/components/LoginForm";
import { AuthHeader } from "@/components/AuthHeader";
import DeviceMonitor from "./pages/DeviceMonitor";
import DeviceDiagnostic from "./pages/DeviceDiagnostic";
import DeviceSetup from "./pages/DeviceSetup";
import FirmwareManager from "./pages/FirmwareManager";
import Index from "./pages/Index";
import Visualize from "./pages/Visualize";
import EmbedVisualization from "./pages/EmbedVisualization";
import { PublicLayout } from "@/components/PublicLayout";
import Statement from "./pages/Statement";
import Recent from "./pages/Recent";
import Media from "./pages/Media";

import Bio from "./pages/Bio";
import Contact from "./pages/Contact";
import NieuwWerk from "./pages/NieuwWerk";
import ArtworkDetail from "./pages/ArtworkDetail";
import Newsletter from "./pages/Newsletter";
import AdminArtworks from "./pages/AdminArtworks";
import { pathFor, type Lang } from "@/i18n/routes";

// Create a React Query client
const queryClient = new QueryClient();

const PublicLogin = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/devices" replace />;
  return <LoginForm />;
};

const AdminPage = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <div className="min-h-screen bg-gray-50">
      <AuthHeader />
      <div className="p-6">{children}</div>
    </div>
  </ProtectedRoute>
);

/** Content routes for one language, mounted under the shared public layout. */
const contentRoutes = (lang: Lang) => (
  <Route key={lang} element={<PublicLayout lang={lang} />}>
    <Route path={pathFor('visualisatie', lang)} element={<div className="p-6"><Visualize /></div>} />
    <Route path={pathFor('statement', lang)} element={<Statement />} />
    <Route path={pathFor('recent', lang)} element={<Recent />} />
    {/* legacy slug */}
    <Route
      path={lang === 'en' ? '/en/recent' : '/recent'}
      element={<Navigate to={pathFor('recent', lang)} replace />}
    />
    <Route path={pathFor('nieuwwerk', lang)} element={<NieuwWerk />} />
    <Route path={`${pathFor('nieuwwerk', lang)}/:slug`} element={<ArtworkDetail />} />
    <Route path={pathFor('media', lang)} element={<Media />} />
    
    <Route path={pathFor('bio', lang)} element={<Bio />} />
    {/* legacy slug */}
    <Route
      path={lang === 'en' ? '/en/bio' : '/bio'}
      element={<Navigate to={pathFor('bio', lang)} replace />}
    />
    <Route path={pathFor('contact', lang)} element={<Contact />} />
    <Route path={pathFor('nieuwsbrief', lang)} element={<Newsletter />} />
  </Route>
);

const PublicApp = () => (
  <Routes>
    <Route element={<PublicLayout lang="nl" />}>
      <Route path="/" element={<Navigate to={pathFor('nieuwwerk', 'nl')} replace />} />
    </Route>
    <Route element={<PublicLayout lang="en" />}>
      <Route path="/en" element={<Navigate to={pathFor('nieuwwerk', 'en')} replace />} />
    </Route>
    <Route path="/login" element={<PublicLogin />} />
    <Route path="/dashboard" element={<AdminPage><Index /></AdminPage>} />
    <Route path="/devices" element={<AdminPage><DeviceMonitor /></AdminPage>} />
    <Route path="/diagnostic" element={<AdminPage><DeviceDiagnostic /></AdminPage>} />
    <Route path="/setup" element={<AdminPage><DeviceSetup /></AdminPage>} />
    <Route path="/firmware" element={<AdminPage><FirmwareManager /></AdminPage>} />
    <Route path="/admin/artworks" element={<AdminPage><AdminArtworks /></AdminPage>} />
    <Route path="/*" element={<Navigate to={pathFor('nieuwwerk', 'nl')} replace />} />
  </Routes>
);

const App = () => (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster />
            <Sonner />
            <Routes>
              <Route path="/embed/:view" element={<EmbedVisualization />} />
              {/* Legacy visualize route redirects to the Dutch public path. */}
              <Route path="/visualize" element={<Navigate to="/visualisatie" replace />} />
              {/* Public content pages — available on every domain, no login. */}
              {contentRoutes('nl')}
              {contentRoutes('en')}
              <Route
                path="/admin/artworks"
                element={<AdminPage><AdminArtworks /></AdminPage>}
              />
              <Route path="/*" element={<PublicApp />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>
);

export default App;

